import React, { useEffect, useMemo, useState } from 'react';
import { Bell, BellOff, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAttractionNotifications } from '../../contexts/AttractionNotificationContext';
import InstallPwaInstructions from '../InstallPwaInstructions';
import {
  MAX_NOTIFICATION_THRESHOLD_MINUTES,
  MIN_NOTIFICATION_THRESHOLD_MINUTES,
  normalizeCustomNotificationThreshold,
} from '../../utils/notificationThresholds';

const THRESHOLDS = [5, 10, 15, 20, 30, 45, 60];

const formatAlertDescription = (alert, t) => {
  if (!alert) return '';
  if (alert.alertType === 'reopen') {
    return t('attractionNotifications.panel.reopenAlert', 'Réouverture');
  }
  return t('attractionNotifications.panel.waitAlert', 'Attente ≤ {{minutes}} min', {
    minutes: alert.thresholdMinutes,
  });
};

const AttractionNotificationModal = ({
  activeAlert,
  context,
  isOpen,
  onClose,
  onLogin,
  onSignup,
  onUpgrade,
  user,
}) => {
  const { t } = useTranslation();
  const {
    actionError,
    actionLoading,
    activeAlerts,
    cancelAlert,
    createAlert,
    error,
    maxActiveAlerts,
    serverReady,
    support,
  } = useAttractionNotifications();
  const [selectedThreshold, setSelectedThreshold] = useState(20);
  const [customThreshold, setCustomThreshold] = useState('');
  const [showWhy, setShowWhy] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const activeCount = activeAlerts.length;
  const serverLimitReached = actionError?.code === 'ACTIVE_ALERT_LIMIT_REACHED';
  const effectiveMaxActiveAlerts = actionError?.maxActiveAlerts || maxActiveAlerts;
  const effectiveActiveCount = serverLimitReached ? (actionError.activeCount ?? activeCount) : activeCount;
  const limitReached = (activeCount >= maxActiveAlerts && !activeAlert) || (serverLimitReached && !activeAlert);
  const upgradeTier = effectiveMaxActiveAlerts < 3 ? 'vip' : 'supporter';
  const upgradeTierLabel = upgradeTier === 'vip'
    ? t('attractionNotifications.plans.vip.label', 'VIP')
    : t('attractionNotifications.plans.supporter.label', 'Membre soutien');
  const normalizedCustomThreshold = normalizeCustomNotificationThreshold(customThreshold);
  const hasCustomThreshold = customThreshold.trim().length > 0;
  const customThresholdInvalid = hasCustomThreshold && normalizedCustomThreshold == null;
  const thresholdToCreate = normalizedCustomThreshold || selectedThreshold;
  const contextKey = `${context?.parkSlug || ''}:${context?.attractionId || ''}`;

  const thresholdOptions = useMemo(() => {
    if (context?.waitTime == null) return THRESHOLDS;
    const belowCurrent = THRESHOLDS.filter((threshold) => threshold < context.waitTime);
    return belowCurrent.length > 0 ? belowCurrent : THRESHOLDS;
  }, [context?.waitTime]);

  useEffect(() => {
    setSuccessMessage(null);
    setShowWhy(false);
    setCustomThreshold('');
    setSelectedThreshold(20);
  }, [contextKey, isOpen]);

  if (!isOpen || !context) return null;

  const handleCreate = async (type, thresholdMinutes = null) => {
    setSuccessMessage(null);
    try {
      await createAlert({ type, thresholdMinutes });
      setSuccessMessage(t('attractionNotifications.modal.created', 'Alerte créée.'));
    } catch (err) {
      void err;
    }
  };

  const handleCancel = async () => {
    if (!activeAlert) return;
    try {
      await cancelAlert(activeAlert.id);
      setSuccessMessage(t('attractionNotifications.modal.cancelled', 'Alerte annulée.'));
    } catch (err) {
      void err;
    }
  };

  return (
    <div className="attraction-notification-overlay" role="dialog" aria-modal="true" aria-labelledby="attraction-notification-title">
      <div className="attraction-notification-modal">
        <div className="attraction-notification-header">
          <div className="attraction-notification-title-block">
            <span className="attraction-notification-kicker">{t('attractionNotifications.modal.kicker', 'Notifications')}</span>
            <h2 id="attraction-notification-title">{context.attractionName}</h2>
            <p className="attraction-notification-park-name">{context.parkName}</p>
          </div>
          <button type="button" className="attraction-notification-close" onClick={onClose} aria-label={t('common.close', 'Fermer')}>
            <X size={20} />
          </button>
        </div>

        {!user ? (
          <div className="attraction-notification-account">
            <Bell size={28} />
            <h3>{t('attractionNotifications.account.title', 'Compte gratuit requis')}</h3>
            <p>{t('attractionNotifications.account.body', 'Crée un compte gratuit pour recevoir des alertes utiles pendant ta visite, retrouver tes favoris sur tous tes appareils et garder tes préférences même si tu changes de téléphone.')}</p>
            <ul>
              <li>{t('attractionNotifications.account.benefitAlerts', 'Alertes de temps d’attente synchronisées entre tes appareils')}</li>
              <li>{t('attractionNotifications.account.benefitFavorites', 'Favoris sauvegardés et disponibles partout')}</li>
              <li>{t('attractionNotifications.account.benefitSettings', 'Préférences et alertes regroupées dans ton espace membre')}</li>
            </ul>
            <div className="attraction-notification-actions">
              <button type="button" className="primary" onClick={onSignup}>{t('attractionNotifications.account.signup', 'Créer un compte gratuit')}</button>
              <button type="button" className="secondary" onClick={onLogin}>{t('attractionNotifications.account.login', 'Se connecter')}</button>
              <button type="button" className="ghost" onClick={onClose}>{t('attractionNotifications.account.later', 'Plus tard')}</button>
            </div>
          </div>
        ) : !support.supported ? (
          <div className="attraction-notification-support">
            <h3>{t('attractionNotifications.unsupported.title', 'Notifications indisponibles ici')}</h3>
            {support.reason === 'ios_pwa_required' ? (
              <>
                <p>{t('attractionNotifications.unsupported.iosPwa', 'Sur iPhone et iPad, les notifications web fonctionnent quand Magic Park Times est ajouté à l’écran d’accueil.')}</p>
                <InstallPwaInstructions compact />
              </>
            ) : (
              <p>{t('attractionNotifications.unsupported.generic', 'Ce navigateur ou ce contexte ne permet pas encore les notifications push.')}</p>
            )}
          </div>
        ) : activeAlert ? (
          <div className="attraction-notification-active">
            <div className="attraction-notification-active-card">
              <Bell size={22} />
              <div>
                <strong>{t('attractionNotifications.modal.activeTitle', 'Alerte active')}</strong>
                <span>{formatAlertDescription(activeAlert, t)}</span>
              </div>
            </div>
            <p>{t('attractionNotifications.modal.oneShot', 'Elle sera automatiquement désactivée après le premier envoi ou en fin de journée.')}</p>
            <div className="attraction-notification-actions">
              <button type="button" className="danger" onClick={handleCancel} disabled={actionLoading}>
                <BellOff size={17} />
                {t('attractionNotifications.modal.cancel', 'Annuler cette alerte')}
              </button>
            </div>
          </div>
        ) : (
          <div className="attraction-notification-config">
            <div className="attraction-notification-limit">
              <span>{t('attractionNotifications.limit.counter', '{{count}}/{{max}} alertes actives', { count: effectiveActiveCount, max: effectiveMaxActiveAlerts })}</span>
              <button type="button" onClick={() => setShowWhy(true)}>
                <Info size={15} />
                {t('attractionNotifications.limit.why', 'Pourquoi {{max}} ?', { max: effectiveMaxActiveAlerts })}
              </button>
            </div>

            {!serverReady && (
              <div className="attraction-notification-warning">
                {t('attractionNotifications.modal.serverUnavailable', 'Le serveur de notifications n’est pas configuré pour le moment.')}
              </div>
            )}

            {limitReached ? (
              <div className="attraction-notification-warning">
                <p>
                  {t(
                    'attractionNotifications.limit.reached',
                    'Tu as atteint la limite de ton niveau actuel ({{max}} alerte active). Annule une alerte ou passe à {{tier}} pour en activer davantage.',
                    { max: effectiveMaxActiveAlerts, tier: upgradeTierLabel }
                  )}
                </p>
                <button
                  type="button"
                  className="attraction-notification-submit"
                  onClick={() => onUpgrade?.(upgradeTier)}
                >
                  {t('attractionNotifications.limit.upgrade', 'Augmenter ma limite')}
                </button>
              </div>
            ) : context.isDown ? (
              <>
                <p>{t('attractionNotifications.modal.reopenCopy', 'Cette attraction est indiquée en panne. Tu peux être prévenu dès qu’elle rouvre.')}</p>
                <button
                  type="button"
                  className="attraction-notification-submit"
                  disabled={actionLoading || !serverReady}
                  onClick={() => handleCreate('reopen')}
                >
                  {t('attractionNotifications.modal.reopenCta', 'Me prévenir à la réouverture')}
                </button>
              </>
            ) : (
              <>
                <p>{t('attractionNotifications.modal.waitCopy', 'Préviens-moi quand l’attente descend à ce seuil ou moins.')}</p>
                <div className="attraction-notification-thresholds">
                  {thresholdOptions.map((threshold) => (
                    <button
                      key={threshold}
                      type="button"
                      className={!hasCustomThreshold && selectedThreshold === threshold ? 'selected' : ''}
                      onClick={() => {
                        setSelectedThreshold(threshold);
                        setCustomThreshold('');
                      }}
                    >
                      {threshold} {t('min')}
                    </button>
                  ))}
                </div>
                <div className="attraction-notification-custom-threshold">
                  <label htmlFor="attraction-notification-custom-threshold">
                    {t('attractionNotifications.customThreshold.label', 'Seuil personnalisé')}
                  </label>
                  <div>
                    <input
                      id="attraction-notification-custom-threshold"
                      type="number"
                      min={MIN_NOTIFICATION_THRESHOLD_MINUTES}
                      max={MAX_NOTIFICATION_THRESHOLD_MINUTES}
                      step="1"
                      inputMode="numeric"
                      value={customThreshold}
                      onChange={(event) => setCustomThreshold(event.target.value)}
                      placeholder={String(selectedThreshold)}
                    />
                    <span>{t('min')}</span>
                  </div>
                  <small>
                    {t('attractionNotifications.customThreshold.help', 'Entre {{min}} et {{max}} minutes.', {
                      min: MIN_NOTIFICATION_THRESHOLD_MINUTES,
                      max: MAX_NOTIFICATION_THRESHOLD_MINUTES,
                    })}
                  </small>
                  {customThresholdInvalid && (
                    <span className="attraction-notification-custom-error">
                      {t('attractionNotifications.customThreshold.error', 'Choisis un nombre entier entre {{min}} et {{max}}.', {
                        min: MIN_NOTIFICATION_THRESHOLD_MINUTES,
                        max: MAX_NOTIFICATION_THRESHOLD_MINUTES,
                      })}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="attraction-notification-submit"
                  disabled={actionLoading || !serverReady || customThresholdInvalid}
                  onClick={() => handleCreate('wait_threshold', thresholdToCreate)}
                >
                  {t('attractionNotifications.modal.waitCta', 'Créer l’alerte')}
                </button>
              </>
            )}
          </div>
        )}

        {showWhy && (
          <div className="attraction-notification-why" role="dialog" aria-modal="true">
            <div>
              <button type="button" className="attraction-notification-close" onClick={() => setShowWhy(false)} aria-label={t('common.close', 'Fermer')}>
                <X size={18} />
              </button>
              <h3>{t('attractionNotifications.limit.modalTitle', 'Pourquoi {{max}} alertes ?', { max: effectiveMaxActiveAlerts })}</h3>
              <p>{t('attractionNotifications.limit.modalBody', 'Les navigateurs limitent de plus en plus les sites qui envoient trop de notifications. Magic Park Times garde donc des alertes rares, ponctuelles et utiles : maximum {{max}} actives, puis suppression automatique après le premier envoi ou en fin de journée.', { max: effectiveMaxActiveAlerts })}</p>
            </div>
          </div>
        )}

        {error && !limitReached && <div className="attraction-notification-error">{error}</div>}
        {successMessage && <div className="attraction-notification-success">{successMessage}</div>}
      </div>
    </div>
  );
};

export default AttractionNotificationModal;
